import { faFileExport } from '@fortawesome/pro-regular-svg-icons'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import { TIMEZONES } from 'shared/constants'
import { downloadZip } from 'shared/helpers'
import { convertToISOFormat } from 'shared/loadSchedulingClient'
import { WizardStepProps } from 'shared/types'
import styled from 'styled-components'
import { formatToConvertPayload } from '../helpers'
import {
  lsErcotSubmissionPreviewTableColumns,
  lsSubmissionPreviewTableColumns,
} from '../schemas'
import { LsCsvRowItem, LsErcotCsvRowItem } from '../types'

const StyledFilterButton = styled(IWButton)`
  width: fit-content;
`

const StyledActionableSection = styled.div`
  display: flex;
  flex-direction: column;

  & > :first-child {
    margin-bottom: 0.25rem;
  }
`

type TableItem = LsCsvRowItem | LsErcotCsvRowItem

type StepState = {
  dayAheadDemandBids: LsCsvRowItem[] | LsErcotCsvRowItem[]
  selectedMarket?: string
}

export type Props = WizardStepProps<StepState>

export default function LSDemandBidPreview({ state, onChange }: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const demandBids = state?.dayAheadDemandBids ?? []
  const [isLoading, setIsLoading] = useState(true)
  const [isConverting, setIsConverting] = useState(false)
  const [items, setItems] = useState<TableItem[]>([])
  const [updatedTableItems, setUpdatedTableItems] = useState<TableItem[]>([])
  const [showConvertButton] = useState(() => {
    const market = state?.selectedMarket

    if (!market) {
      return false
    }

    const markets = Object.keys(TIMEZONES).filter(
      (k) => k !== 'ERCOT' && k !== 'MISO',
    )

    return markets.includes(market.toUpperCase())
  })

  const tableColumns =
    state?.selectedMarket?.toUpperCase() === 'ERCOT'
      ? lsErcotSubmissionPreviewTableColumns
      : lsSubmissionPreviewTableColumns

  const toTableItems = (bids: LsCsvRowItem[] | LsErcotCsvRowItem[]) => {
    const orderedItems = orderBy(bids || [], [
      ['market', 'brand', 'zone', 'location_name', 'location'],
    ])
    return orderedItems as TableItem[]
  }

  const handleConvert = async () => {
    setIsConverting(true)
    const TOAST_DURATION = 30_000
    const toastId = toast.grey(t('loadScheduling.toasts.converting'), {
      duration: TOAST_DURATION,
    })

    try {
      const payload = formatToConvertPayload(
        updatedTableItems as LsCsvRowItem[],
      )
      const response = await convertToISOFormat(payload)
      const filename = `ls-files-${Date.now()}`
      await downloadZip(response, filename, false)
    } catch (error) {
      toast.alert(t('loadScheduling.toasts.conversionError'))
    } finally {
      toast.removeToast(toastId)
      setIsConverting(false)
    }
  }

  const renderTopRightComponent = () => {
    if (!showConvertButton) {
      return
    }

    return (
      <StyledFilterButton
        disabled={isConverting}
        onClick={() => handleConvert()}
        icon={faFileExport}
        iconPosition="leading"
      >
        {t('button.convert')}
      </StyledFilterButton>
    )
  }

  useEffect(() => {
    if (demandBids?.length) {
      setItems(toTableItems(demandBids))
      setIsLoading(false)
    }
  }, [demandBids])

  useEffect(() => {
    onChange(
      {
        dayAheadDemandBids: updatedTableItems as
          | LsCsvRowItem[]
          | LsErcotCsvRowItem[],
      },
      true,
    )
  }, [updatedTableItems])

  return (
    <>
      <StyledActionableSection>
        <IWTypography
          size="lg"
          weight="medium"
          fontHue={{ color: 'grey', value: 900 }}
        >
          {t('loadScheduling.titles.submissionPreview')}
        </IWTypography>

        <IWTypography fontHue={{ color: 'grey', value: 500 }} size="sm">
          {t('loadScheduling.descriptions.submissionPreview')}
        </IWTypography>
      </StyledActionableSection>

      {isLoading && <IWLoading />}

      {Boolean(demandBids?.length) && !isLoading && (
        <IWGenericTable
          border
          items={items}
          columns={tableColumns}
          pinnedColumns={{ brand: 'left' }}
          showColumnSettings
          onTableItemsUpdated={(updatedItems) => {
            setUpdatedTableItems(updatedItems)
          }}
          topRightComponent={renderTopRightComponent()}
        />
      )}
    </>
  )
}
